import { Box, BoxProps, useTheme } from "@mui/material";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";

const TRACK_WIDTH_PX = 8;
const MIN_THUMB_PX = 28;

const hideNativeScrollbar = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
} as const;

export type NavDrawerScrollSurfaceProps = BoxProps & {
  children: React.ReactNode;
};

/**
 * Scroll surface with native scrolling (wheel, touch, keyboard focus on children) but no layout
 * gutter: native scrollbars are hidden and a thin thumb is drawn in an absolutely positioned track.
 */
export function NavDrawerScrollSurface({ children, sx, ...rest }: NavDrawerScrollSurfaceProps) {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [metrics, setMetrics] = useState({ scrollTop: 0, scrollHeight: 0, clientHeight: 0 });

  const thumbBase = theme.palette.nav.scrollThumb;
  const thumbHoverColor = theme.palette.nav.scrollThumbHover;

  const readMetrics = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMetrics({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el) return;

    readMetrics();
    const onScroll = () => readMetrics();
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(readMetrics);
    ro.observe(el);
    if (content) {
      ro.observe(content);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [readMetrics]);

  const { scrollTop, scrollHeight, clientHeight } = metrics;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const canScroll = maxScroll > 0;

  const thumbHeight = canScroll
    ? Math.max(MIN_THUMB_PX, (clientHeight / scrollHeight) * clientHeight)
    : 0;
  const thumbTravel = Math.max(0, clientHeight - thumbHeight);
  const thumbTop = canScroll && thumbTravel > 0 ? (scrollTop / maxScroll) * thumbTravel : 0;

  const thumbVisible = canScroll && (hovered || dragging);

  const onThumbMouseDown = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = scrollRef.current;
      if (!el) return;

      const ch = el.clientHeight;
      const sh = el.scrollHeight;
      const scrollRange = sh - ch;
      if (scrollRange <= 0) return;

      const th = Math.max(MIN_THUMB_PX, (ch / sh) * ch);
      const trackLen = Math.max(1, ch - th);

      const startY = e.clientY;
      const startScrollTop = el.scrollTop;

      setDragging(true);

      const onMove = (ev: MouseEvent) => {
        const dy = ev.clientY - startY;
        el.scrollTop = Math.min(
          scrollRange,
          Math.max(0, startScrollTop + (dy / trackLen) * scrollRange)
        );
      };

      const onUp = () => {
        setDragging(false);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [scrollRef]
  );

  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  return (
    <Box
      {...rest}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        backgroundColor: theme.palette.nav.background,
        ...sx,
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          height: "100%",
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          ...hideNativeScrollbar,
        }}
      >
        <Box ref={contentRef} sx={{ minHeight: "100%" }}>
          {children}
        </Box>
      </Box>

      {canScroll ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: TRACK_WIDTH_PX,
            pointerEvents: "none",
          }}
        >
          <Box
            onMouseDown={onThumbMouseDown}
            sx={{
              position: "absolute",
              left: 1,
              right: 1,
              height: thumbHeight,
              top: thumbTop,
              borderRadius: 999,
              backgroundColor: dragging ? thumbHoverColor : thumbBase,
              pointerEvents: thumbVisible ? "auto" : "none",
              cursor: dragging ? "grabbing" : "grab",
              opacity: thumbVisible ? 1 : 0,
              transition: theme.transitions.create("opacity", {
                duration: theme.transitions.duration.shorter,
              }),
              "&:hover": {
                backgroundColor: thumbHoverColor,
              },
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}
